/* =============================================================================
   As strings da aplicação, em português do Brasil
   -----------------------------------------------------------------------------
   Typed against the English catalogue's keys, so this file cannot drift from it
   in either direction: a missing key is a compile error, and an extra one is
   too. A half-translated screen — one English phrase left among Portuguese — is
   the defect this catches, and it is the one a review reads past.

   Written as a Brazilian construction and finance professional writes, not as a
   word-for-word rendering of the English:
   empreendimento rather than projeto for a development, aporte for a capital
   contribution, cronograma for a schedule, evidência for evidence, fornecedor
   for a supplier, vistoria for an inspection, alvará for a permit. Second person
   singular (`você`), sentence case, and the same formal register as the English.

   Proper nouns stay as they are: brand names, provider names and the raw codes
   the API sends.
   ============================================================================= */

import type { AppTranslationKey } from './en';

export const ptBR: Record<AppTranslationKey, string> = {
  /* ---- Shared vocabulary --------------------------------------------------- */
  'common.all': 'Todos',
  'common.search': 'Buscar',
  'common.date': 'Data',
  'common.amount': 'Valor',
  'common.status': 'Situação',
  'common.total': 'Total',

  /* ---- Domain vocabulary --------------------------------------------------- */
  'status.phase.planned': 'Planejado',
  'status.phase.completed': 'Concluído',
  'status.phase.inProgress': 'Em andamento',
  'status.publication.draft': 'Rascunho',
  'status.publication.inReview': 'Em revisão',
  'status.publication.approved': 'Aprovado',
  'status.publication.published': 'Publicado',
  'status.verification.verified': 'Verificado',
  'status.verification.inReview': 'Em revisão',
  'status.verification.rejected': 'Rejeitado',
  'status.participation.confirmed': 'Confirmado',
  'status.participation.pending': 'Pendente',
  'status.participation.draft': 'Rascunho',
  'status.investor.active': 'Ativo',
  'status.investor.prospect': 'Prospecto',
  'status.investor.closed': 'Encerrado',
  'status.investorKind.fund': 'Fundo',
  'status.investorKind.individual': 'Pessoa física',
  'status.investorKind.trust': 'Truste',
  'status.evidenceKind.photo': 'Foto',
  'status.evidenceKind.video': 'Vídeo',
  'status.evidenceKind.note': 'Nota',
  'status.documentType.invoice': 'Nota fiscal',
  'status.documentType.receipt': 'Recibo',
  'status.documentType.contract': 'Contrato',
  'status.documentType.permit': 'Alvará',
  'status.documentType.quote': 'Orçamento',
  'status.documentType.report': 'Relatório',
  'status.documentType.inspection': 'Vistoria',
  'status.lifecycle.capitalRaising': 'Preparando captação',
  'status.lifecycle.acquisition': 'Em aquisição',
  'status.lifecycle.construction': 'Em obras',
  'status.lifecycle.viability': 'Em estudo de viabilidade',
  'status.lifecycle.opportunity': 'Em prospecção',
  'status.lifecycle.saleAndClose': 'Em venda',

  /* ---- Navigation ---------------------------------------------------------- */
  'nav.group.overview': 'Visão geral',
  'nav.group.delivery': 'Execução',
  'nav.group.money': 'Finanças',
  'nav.group.relationships': 'Relacionamento',
  'nav.group.publishing': 'Publicação',
  'nav.group.administration': 'Administração',
  'nav.platform.dashboard': 'Painel',
  'nav.platform.dashboardHint': 'A carteira em um relance',
  'nav.platform.opportunities': 'Oportunidades',
  'nav.platform.opportunitiesHint': 'Pipeline e viabilidade',
  'nav.platform.projects': 'Empreendimentos',
  'nav.platform.projectsHint': 'Todos os empreendimentos',
  'nav.platform.schedule': 'Cronograma',
  'nav.platform.scheduleHint': 'Etapas e marcos',
  'nav.platform.evidence': 'Evidências',
  'nav.platform.evidenceHint': 'Fotos, notas e arquivos',
  'nav.platform.financials': 'Financeiro',
  'nav.platform.financialsHint': 'Aportes, despesas e saldos',
  'nav.platform.suppliers': 'Fornecedores',
  'nav.platform.suppliersHint': 'Prestadores e parceiros',
  'nav.platform.investors': 'Investidores',
  'nav.platform.investorsHint': 'Participações e acessos',
  'nav.platform.publications': 'Publicações',
  'nav.platform.publicationsHint': 'Revisar, aprovar, liberar',
  'nav.platform.reports': 'Relatórios',
  'nav.platform.reportsHint': 'Gerados a partir da base',
  'nav.platform.notifications': 'Notificações',
  'nav.platform.notificationsHint': 'O que foi comunicado aos investidores',
  'nav.platform.permissions': 'Permissões',
  'nav.platform.permissionsHint': 'Perfis e controles',
  'nav.portal.projects': 'Meus empreendimentos',
  'nav.portal.projectsHint': 'Suas participações',
  'nav.portal.updates': 'Atualizações',
  'nav.portal.updatesHint': 'Relatórios publicados',
  'nav.portal.documents': 'Documentos',
  'nav.portal.documentsHint': 'Arquivos autorizados',
  'nav.portal.opportunities': 'Oportunidades',
  'nav.portal.opportunitiesHint': 'Empreendimentos futuros',
  'nav.section.platform': 'Seções da plataforma',
  'nav.section.portal': 'Seções do portal do investidor',
  'nav.account': 'Conta',
  'nav.signOut': 'Sair',
  'nav.fallback': 'Empreendimento',
  'nav.signIn': 'Entrar',
  'nav.signingIn': 'Entrando',
  'nav.tab.overview': 'Visão geral',
  'nav.tab.schedule': 'Cronograma',
  'nav.tab.financials': 'Financeiro',
  'nav.tab.evidence': 'Evidências',
  'nav.tab.investors': 'Investidores',
  'nav.tab.publications': 'Publicações',

  /* ---- Platform shell ----------------------------------------------------- */
  'shell.notifications.none': 'Nenhuma notificação não lida.',
  'shell.notifications.unread.one': '{count} notificação não lida.',
  'shell.notifications.unread.other': '{count} notificações não lidas.',
  'shell.notifications.markAllRead': 'Marcar todas como lidas',
  'shell.notifications.viewAll': 'Ver todas',
  'shell.notifications.nothing': 'Nada a relatar.',

  /* ---- Investor portal shell ---------------------------------------------- */
  'shell.portal.tagline': 'Portal do investidor',
  'shell.portal.access': 'Acesso do investidor',
  'shell.portal.accountNamed': 'Conta: {name}',
  'shell.portal.role': 'Investidor',

  /* ---- Project shell ------------------------------------------------------ */
  'project.shell.notFoundTitle': 'Empreendimento não encontrado',
  'project.shell.notFoundHeading': 'Empreendimento inexistente',
  'project.shell.notFoundMessage':
    'Nada corresponde a “{id}”. Ele pode ter sido encerrado ou arquivado.',
  'project.shell.sale': 'Venda {date}',
  'project.shell.next': 'Próximo: {phase} · início previsto em {date}',
  'project.shell.progress': '{name} — progresso geral',
  'project.shell.sections': 'Seções de {name}',
  'project.shell.committed': 'Capital comprometido',
  'project.shell.contributed': 'Aportes realizados',
  'project.shell.spent': 'Gasto até o momento',
  'project.shell.variance': 'Variação nas obras iniciadas',
  'project.shell.uncalled': '{amount} ainda a chamar',
  'project.shell.fullyCalled': 'Chamadas concluídas',
  'project.shell.unspent': '{amount} não gasto',
  'project.shell.varianceBasis': 'sobre {amount} orçados para as etapas em andamento',

  /* ---- Sign-in ------------------------------------------------------------ */
  'login.skip': 'Ir para o login',
  'login.heading': 'Entrar',
  'login.continueGoogle': 'Continuar com o Google',
  'login.continueApple': 'Continuar com a Apple',
  'login.continueMicrosoft': 'Continuar com a Microsoft',
  'login.emailSwitch': 'Entrar com e-mail e senha',
  'login.email': 'E-mail',
  'login.emailPlaceholder': 'voce@exemplo.com',
  'login.password': 'Senha',
  'login.forgot': 'Esqueceu a senha?',
  'login.submit': 'Entrar',
  'login.backToSocial': 'Voltar para o login social',
  'login.resetHeading': 'Redefinir sua senha',
  'login.sendResetLink': 'Enviar link de redefinição',
  'login.backToLogin': 'Voltar para o login',
  'login.notConnectedTitle': 'Login por e-mail não está conectado',
  'login.notConnectedBody':
    'Nada foi enviado para {email}. Continue com Google, Microsoft ou Apple.',
  'login.resetNotSentTitle': 'Link de redefinição não enviado',
  'login.resetNotSentBody':
    'Esta versão não tem serviço de redefinição de senha conectado, então nada foi enviado para {email}.',

  /* ---- Provider callback -------------------------------------------------- */
  'auth.signingInHeading': 'Entrando',
  'auth.signingIn': 'Entrando na sua conta',
  'auth.failedTitle': 'Login não concluído',
  'auth.emptyTitle': 'Nenhum resultado de login',
  'auth.emptyMessage': 'Entre pela página inicial do escrowbuild.io para continuar.',
  'auth.backToSignIn': 'Voltar para o login',
  'auth.providerReturned': 'O provedor retornou {reason}. Entre novamente para continuar.',
  'auth.notCompleted': 'O login não foi concluído. Entre novamente para continuar.',
  'auth.unverified':
    'Não foi possível verificar este login em relação ao que foi iniciado, então ele não foi concluído. Entre novamente para continuar.',
  'auth.networkError':
    'Não foi possível falar com o servidor. Verifique sua conexão e tente de novo.',
  'auth.exchangeFailed': 'Não foi possível concluir o login (HTTP {status}).',

  /* ---- 404 ---------------------------------------------------------------- */
  'notFound.heading': 'Página não encontrada',
  'notFound.title': 'Esta página não existe',
  'notFound.description':
    'O endereço que você acessou não é uma das telas da plataforma. Ele pode ter sido renomeado desde que o link foi salvo, ou o endereço pode ter sido digitado incorretamente.',
  'notFound.dashboard': 'Voltar para o painel',
  'notFound.portal': 'Abrir o portal do investidor',

  /* ---- Opportunities ------------------------------------------------------ */
  'opportunities.title': 'Oportunidades',
  'opportunities.scaffold': 'Estrutura inicial — conteúdo ainda a preencher.',
  'opportunities.placeholder': 'Espaço reservado.',

  /* ==== GROUPED CATALOGUE ENTRIES (merged) ==== */

  /* ---- Dashboard, projects and the project overview (GA) ---- */
  'dashboard.title': 'Painel da carteira',
  'dashboard.subtitle.one':
    '{count} empreendimento sob gestão — capital, custo incorrido e situação de publicação em toda a carteira.',
  'dashboard.subtitle.other':
    '{count} empreendimentos sob gestão — capital, custo incorrido e situação de publicação em toda a carteira.',
  'dashboard.committed': 'Capital comprometido total',
  'dashboard.totalSpent': 'Custo incorrido total',
  'dashboard.unspent': 'Saldo não utilizado total',
  'dashboard.active': 'Empreendimentos ativos',
  'dashboard.committedHint': '{amount} em aportes realizados até o momento',
  'dashboard.unspentHint': 'Aportes realizados menos o que foi efetivamente gasto.',
  'dashboard.varianceHint': 'Frente a {amount} orçados nas etapas em andamento.',
  'dashboard.activeHint': 'Empreendimentos com uma etapa em andamento no momento.',
  'dashboard.showAlert': 'Mostrar alerta novamente',
  'dashboard.attention.title.one': '{count} item ainda não pode chegar aos investidores',
  'dashboard.attention.title.other': '{count} itens ainda não podem chegar aos investidores',
  'dashboard.attention.draft': '{project} — "{title}" ainda é rascunho referente a {period}.',
  'dashboard.attention.inReview': '{project} — "{title}" está em revisão referente a {period}.',
  'dashboard.attention.expense':
    '{project} — {vendor}, no valor de {amount}, aguarda análise financeira.',
  'dashboard.attention.document': '{project} — "{name}" foi rejeitado e precisa ser substituído.',
  'dashboard.progress': 'Progresso geral',
  'dashboard.spent': 'Custo incorrido',
  'dashboard.balance': 'Saldo',
  'dashboard.updates.title': 'Atualizações publicadas recentemente',
  'dashboard.updates.description':
    'As cinco atualizações mais recentes liberadas para o portal do investidor.',
  'projects.title': 'Empreendimentos',
  'projects.subtitle.one':
    'A carteira — {count} empreendimento ativo, com capital comprometido, custo incorrido até o momento, saldo e variação frente ao orçamento por etapa.',
  'projects.subtitle.other':
    'A carteira — {count} empreendimentos ativos, com capital comprometido, custo incorrido até o momento, saldo e variação frente ao orçamento por etapa.',
  'projects.panelTitle': 'Carteira',
  'projects.panelDescription':
    'Cada valor é derivado do cronograma e das participações do próprio empreendimento.',
  'projects.searchLabel': 'Buscar empreendimentos',
  'projects.searchPlaceholder': 'Nome, endereço ou cidade',
  'projects.phase': 'Etapa',
  'projects.matched.one': '{matched} de {count} empreendimento',
  'projects.matched.other': '{matched} de {count} empreendimentos',
  'projects.clearFilters': 'Limpar filtros',
  'projects.column.address': 'Endereço / cidade',
  'projects.column.progress': 'Progresso',
  'projects.column.spent': 'Custo incorrido',
  'projects.column.balance': 'Saldo',
  'projects.column.variance': 'Variação',
  'projects.column.projectedSale': 'Venda prevista',
  'projects.sort.asc':
    'Classificado em ordem crescente. Ative para classificar em ordem decrescente.',
  'projects.sort.desc': 'Classificado em ordem decrescente. Ative para limpar a classificação.',
  'projects.sort.none': 'Classificar em ordem crescente.',
  'projects.empty.title': 'Nenhum empreendimento corresponde a estes filtros',
  'projects.empty.description':
    'Nada na carteira corresponde à busca e à etapa atuais. Limpe os filtros para ver os {count} empreendimentos.',
  'projects.caption':
    'A carteira: cada empreendimento com sua etapa, progresso, capital comprometido, custo incorrido até o momento, saldo não utilizado, variação frente ao orçamento por etapa e venda prevista.',
  'project.overview.notFoundMessage':
    'Nada corresponde a "{id}". Ele pode ter sido encerrado ou arquivado.',
  'project.overview.currentPhase.title': 'Etapa atual',
  'project.overview.currentPhase.description':
    'As posições na linha do tempo são meses no eixo de 12 meses do cronograma.',
  'project.overview.plannedSpan': 'Período previsto',
  'project.overview.actualSpan': 'Período realizado',
  'project.overview.notStarted': 'Não iniciada',
  'project.overview.budgeted': 'Orçado',
  'project.overview.variance': 'Variação',
  'project.overview.evidenceFiled': 'Evidências arquivadas',
  'project.overview.evidenceCount.one': '{count} item',
  'project.overview.evidenceCount.other': '{count} itens',
  'project.overview.evidenceCleared.one': '{count} liberada para o portal',
  'project.overview.evidenceCleared.other': '{count} liberadas para o portal',
  'project.overview.months.one': '{months} mês de duração',
  'project.overview.months.other': '{months} meses de duração',
  'project.overview.spendProgress': '{phase} — custo incorrido frente ao orçamento por etapa',
  'project.overview.noBudget': 'Esta etapa não tem orçamento próprio.',
  'project.overview.noPhase.title': 'Nenhuma etapa a relatar',
  'project.overview.noPhase.description':
    'O cronograma deste empreendimento não tem etapa em andamento nem etapa planejada.',
  'project.overview.milestones.title': 'Próximos marcos',
  'project.overview.milestones.description': 'Os mais próximos primeiro.',
  'project.overview.milestones.list': 'Marcos futuros',
  'project.overview.milestones.empty': 'Não há marcos programados à frente.',
  'project.overview.phasesLink.one': 'Ver todas as {count} etapas no cronograma',
  'project.overview.phasesLink.other': 'Ver todas as {count} etapas no cronograma',
  'project.overview.investorNotified': 'Investidor notificado',
  'project.overview.activity.title': 'Atividade recente',
  'project.overview.activity.description': 'As cinco entradas mais recentes.',
  'project.overview.activity.published': 'Atualização publicada',
  'project.overview.activity.milestone': 'Marco alcançado',
  'project.overview.activity.empty': 'Nada foi publicado e nenhum marco foi alcançado ainda.',
  'project.overview.participation.title': 'Participação dos investidores',
  'project.overview.participation.description.one':
    '{count} participação em {project}, e quanto cada uma já aportou.',
  'project.overview.participation.description.other':
    '{count} participações em {project}, e quanto cada uma já aportou.',
  'project.overview.participation.emptyTitle': 'Nenhuma participação de investidor',
  'project.overview.participation.emptyDescription':
    'Nenhuma participação com quota registrada neste empreendimento.',
  'project.overview.participation.caption':
    'Participações de investidores neste empreendimento, com a quota, o capital comprometido e os aportes realizados de cada uma.',
  'project.overview.participation.summary':
    '{investorShare} detidos pelos investidores acima, mais {ownership} retidos pela construtora, correspondem a {total} do empreendimento. Os aportes somam {contributed}, frente a um capital comprometido de {committed}.',
  'project.overview.column.investor': 'Investidor',
  'project.overview.column.share': 'Quota',
  'project.overview.column.committed': 'Comprometido',
  'project.overview.column.document': 'Documento',
  'project.overview.column.type': 'Tipo',
  'project.overview.column.review': 'Revisão',
  'project.overview.column.publication': 'Publicação',
  'project.overview.investorVisible': 'Visível ao investidor',
  'project.overview.internalOnly': 'Somente interno',
  'project.overview.documents.title': 'Documentos mais recentes',
  'project.overview.documents.description.one':
    '{count} arquivado. Apenas documentos aprovados na revisão chegam ao investidor.',
  'project.overview.documents.description.other':
    '{count} arquivados. Apenas documentos aprovados na revisão chegam ao investidor.',
  'project.overview.documents.filterLabel': 'Filtrar documentos por situação de revisão',
  'project.overview.documents.emptyTitle': 'Nada está {state}',
  'project.overview.documents.emptyDescription':
    'Todos os documentos deste empreendimento estão em outra situação de revisão.',
  'project.overview.documents.showAll': 'Mostrar todos os documentos',
  'project.overview.documents.caption':
    'Os documentos mais recentes deste empreendimento, com tipo, data, situação de revisão e se o investidor pode vê-los.',
  'project.overview.documents.showing.one':
    'Os {shown} mais recentes de {count} documento nesta situação.',
  'project.overview.documents.showing.other':
    'Os {shown} mais recentes de {count} documentos nesta situação.',

  /* ---- Schedule and evidence (GB) ---- */
  'schedule.title': 'Cronograma',
  'schedule.subtitle':
    'As etapas de todos os empreendimentos em um único eixo, o previsto contra o realizado. O plano não é uma sequência — as etapas correm em paralelo, e a sobreposição é desenhada em vez de escondida.',
  'schedule.axis.month': 'Mês {month}',
  'schedule.metric.complete': 'Etapas concluídas',
  'schedule.metric.completeHint': 'Em {name}.',
  'schedule.metric.concurrent': 'Frentes simultâneas',
  'schedule.metric.concurrentHint':
    'Etapas em execução simultânea por volta do mês {month} do cronograma.',
  'schedule.metric.noOverlap': 'Nenhuma etapa se sobrepõe neste cronograma.',
  'schedule.pastPlannedEnd': 'Além do fim previsto',
  'schedule.metric.lateHint': 'Etapas cujo período realizado ultrapassa o previsto.',
  'schedule.timeline.title': 'Linha do tempo',
  'schedule.timeline.description':
    'As posições na base são percentuais de um intervalo de 12 meses; o eixo é marcado em meses a partir do início do cronograma de cada empreendimento.',
  'schedule.development': 'Empreendimento',
  'schedule.forecast': '{name} · {city} · venda prevista para {date}',
  'schedule.hideMilestones': 'Ocultar marcos',
  'schedule.showMilestones': 'Mostrar marcos',
  'schedule.peak.title.one': '{count} etapa em execução simultânea por volta do mês {month}',
  'schedule.peak.title.other': '{count} etapas em execução simultânea por volta do mês {month}',
  'schedule.peak.description':
    '{names}. O §10 do documento é explícito quanto à sobreposição de etapas: elétrica e hidráulica correm juntas, a estrutura avança enquanto outra área recebe revestimento externo, e o drywall avança em um pavimento enquanto a estrutura continua em outro.',
  'schedule.late.title.one': '{count} etapa se estendeu além do fim previsto',
  'schedule.late.title.other': '{count} etapas se estenderam além do fim previsto',
  'schedule.late.item': '{name} em {months} meses',
  'schedule.noLate.title': 'Todas as etapas iniciadas fecharam dentro da janela prevista',
  'schedule.noLate.description': 'Nenhum período realizado ultrapassa a data de término prevista.',
  'schedule.noProject': 'Nenhum empreendimento selecionado.',
  'schedule.plannedVsActual.title': 'Previsto contra realizado',
  'schedule.plannedVsActual.description':
    'Uma linha por etapa, com o desvio e os valores por trás dele.',
  'schedule.table.caption':
    'Etapas de {name}: janelas prevista e realizada, desvio, orçamento e custo incorrido',
  'schedule.col.phase': 'Etapa',
  'schedule.col.plannedWindow': 'Janela prevista',
  'schedule.col.actualWindow': 'Janela realizada',
  'schedule.col.drift': 'Desvio',
  'schedule.col.budgeted': 'Orçado',
  'schedule.col.actual': 'Realizado',
  'schedule.col.variance': 'Variação',
  'schedule.col.spend': 'Custo incorrido',
  'schedule.notStarted': 'Não iniciada',
  'schedule.drift.value': '{value} mês',
  'schedule.table.note':
    'O desvio é o período realizado medido contra o previsto: um valor positivo indica uma etapa que passou do plano, um negativo indica término antecipado. A última coluna é o custo incorrido contra o orçamento da própria etapa, não o do empreendimento.',
  'schedule.milestones.title': 'Marcos',
  'schedule.milestones.description': 'Datas e comunicação ao investidor em {name}.',
  'schedule.milestones.selected': 'o empreendimento selecionado',
  'schedule.milestones.empty': 'Nenhum marco registrado para este empreendimento.',
  'schedule.milestone.reached': 'Alcançado',
  'schedule.milestone.upcoming': 'Previsto',
  'schedule.milestone.notified': 'Investidor notificado',
  'schedule.milestone.notNotified': 'Não notificado',
  'schedule.portfolio.title': 'Carteira',
  'schedule.portfolio.description':
    'Os mesmos números para todos os empreendimentos, para que os três possam ser comparados entre si.',
  'schedule.portfolio.caption':
    'Todos os empreendimentos: contagem de etapas, orçamento, custo incorrido e etapas em atraso',
  'schedule.portfolio.col.development': 'Empreendimento',
  'schedule.portfolio.col.done': 'Concluídas',
  'schedule.portfolio.col.running': 'Em andamento',
  'schedule.portfolio.col.planned': 'Planejadas',
  'schedule.portfolio.col.budgeted': 'Orçado',
  'schedule.portfolio.col.actual': 'Realizado',
  'schedule.portfolio.col.late': 'Em atraso',
  'schedule.gantt.planned': 'previsto {window}',
  'schedule.gantt.notStarted': 'não iniciada',
  'schedule.gantt.actual': 'realizado {window}',
  'schedule.gantt.late': '{months} meses além do previsto',
  'schedule.gantt.budgeted': 'Orçado {amount}',
  'schedule.gantt.spent': 'Gasto {amount}',
  'schedule.legend.title': 'Legenda',
  'schedule.startedEarly': 'Iniciada antes do previsto',
  'schedule.legend.milestone': 'Marco',
  'schedule.legend.overlap': 'Linhas sobrepostas são etapas em execução ao mesmo tempo',
  'project.schedule.filter.all': 'Todas as etapas',
  'project.schedule.timeline.title': 'Linha do tempo das etapas',
  'project.schedule.timeline.description':
    'Todas as etapas em um único eixo. A ordem das linhas é a ordem do registro, não a ordem da obra — espera-se que as etapas se sobreponham.',
  'project.schedule.filterLabel': 'Filtrar etapas por situação',
  'project.schedule.empty.title': 'Nenhuma etapa registrada',
  'project.schedule.empty.description':
    'Este empreendimento ainda não tem cronograma, então não há nada para posicionar no eixo.',
  'project.schedule.col.phase': 'Etapa',
  'project.schedule.noMatch': 'Nenhuma etapa tem esta situação.',
  'project.schedule.legend.actual': 'Realizado',
  'project.schedule.legend.reached': 'Marco alcançado',
  'project.schedule.legend.upcoming': 'Marco previsto',
  'project.schedule.note.axis':
    'As posições são percentuais de um eixo de 12 meses — uma divisão equivale a um mês.',
  'project.schedule.note.reach':
    'Este cronograma chega ao mês {month}, então o eixo é estendido para cobri-lo.',
  'project.schedule.note.spent': '{value} do orçamento por etapa já foi gasto.',
  'project.schedule.parallel.title': 'Em execução paralela',
  'project.schedule.parallel.none':
    'Nenhuma etapa nesta visualização compartilha uma janela de execução.',
  'project.schedule.parallel.summary.one':
    '{count} par sobreposto entre as {phases} etapas exibidas; no máximo {fronts} frentes abertas ao mesmo tempo.',
  'project.schedule.parallel.summary.other':
    '{count} pares sobrepostos entre as {phases} etapas exibidas; no máximo {fronts} frentes abertas ao mesmo tempo.',
  'project.schedule.parallel.rules':
    'Cada etapa é lida pela janela realizada depois de iniciada, e pela janela prevista antes disso.',
  'project.schedule.parallel.with': 'com',
  'project.schedule.budget.title': 'Orçamento por etapa',
  'project.schedule.budget.description':
    'Orçado contra realizado em cada etapa, para que o total confira com o número exibido no cabeçalho do empreendimento.',
  'project.schedule.budget.caption':
    'Custo orçado e realizado por etapa, com o total do empreendimento.',
  'project.schedule.col.budgeted': 'Orçado',
  'project.schedule.col.actual': 'Realizado',
  'project.schedule.col.variance': 'Variação',
  'project.schedule.total': 'Total do empreendimento',
  'project.schedule.amount.budgeted': '{amount} orçado',
  'project.schedule.amount.actual': '{amount} realizado',
  'project.schedule.amount.variance': '{amount} de variação',
  'project.schedule.sr.planned': 'Meses previstos de {from} a {to}.',
  'project.schedule.sr.actual': 'Meses realizados de {from} a {to}.',
  'project.schedule.sr.notStarted': 'Não iniciada.',
  'project.schedule.sr.figures': 'Orçado {budgeted}, realizado {actual}, variação {variance}.',
  'project.schedule.sr.milestones': 'Marcos: {list}.',
  'project.schedule.sr.milestone': '{title} ({date}, {status})',
  'project.schedule.milestone.reached': 'alcançado',
  'project.schedule.milestone.upcoming': 'previsto',
  'project.schedule.notStarted': 'Não iniciada',
  'project.schedule.month': 'mês {month}',
  'project.schedule.monthRange': 'meses {from}–{to}',
  'evidence.title': 'Evidências',
  'evidence.subtitle':
    'Todas as fotos, notas e arquivos capturados na obra. Capturar e liberar são duas decisões diferentes: um documento pode estar verificado internamente e ainda assim não estar visível para o investidor.',
  'evidence.metric.items': 'Itens de evidência',
  'evidence.metric.itemsHint': '{released} liberados para investidores.',
  'evidence.metric.documents': 'Documentos arquivados',
  'evidence.metric.documentsHint': '{released} liberados em {projects} empreendimentos.',
  'evidence.metric.awaiting': 'Aguardando publicação',
  'evidence.metric.awaitingHint': 'Fotos e documentos capturados, mas ainda não liberados.',
  'evidence.metric.verifiedUnreleased': 'Verificados, mas não liberados',
  'evidence.metric.verifiedUnreleasedHint':
    'Passaram pela verificação interna e estão retidos no portal.',
  'evidence.gate.title': 'Controle de publicação',
  'evidence.gate.description':
    'O que é capturado não é o que o investidor vê — liberar é um ato separado, e nada chega ao portal antes disso.',
  'evidence.gate.showEverything': 'Mostrar tudo',
  'evidence.gate.showOnlyUnpublished': 'Mostrar apenas não publicados',
  'evidence.gate.alert.title': '{awaiting} de {total} registros ainda não foram publicados',
  'evidence.gate.alert.description':
    '{documents} documentos e {evidence} itens de evidência são capturados internamente e ficam invisíveis para os investidores. Desses documentos, {verified} já passaram pela verificação — estar verificado e estar liberado são dois estados distintos, e apenas o segundo abre o portal.',
  'evidence.gate.phase.title': 'Todos os documentos estão vinculados a uma etapa',
  'evidence.gate.phase.description':
    'Todos os {documents} documentos têm empreendimento e etapa, então nenhum é retido pela regra de vinculação de etapa.',
  'evidence.gate.noPhase.title.one': '{count} documento sem etapa',
  'evidence.gate.noPhase.title.other': '{count} documentos sem etapa',
  'evidence.gate.noPhase.description': '{names}. Um registro sem etapa não pode ser publicado.',
  'evidence.gate.heldBack': 'Retidos',
  'evidence.notReleased': 'Não liberado',
  'evidence.notPublished': 'Não publicado',
  'evidence.filters.title': 'Filtros',
  'evidence.filters.description':
    'Estes filtros restringem a galeria e o registro abaixo ao mesmo tempo.',
  'evidence.filter.project': 'Empreendimento',
  'evidence.filter.allProjects': 'Todos os empreendimentos',
  'evidence.filter.publication': 'Publicação',
  'evidence.filter.anyState': 'Qualquer situação',
  'evidence.filter.phase': 'Etapa',
  'evidence.filter.allPhases': 'Todas as etapas',
  'evidence.filter.kind': 'Categoria',
  'evidence.filter.allKinds': 'Todas as categorias',
  'evidence.showing':
    'Exibindo {evidence} de {totalEvidence} itens de evidência e {documents} de {totalDocuments} documentos.',
  'evidence.clearFilters': 'Limpar filtros',
  'evidence.gallery.title': 'Fotos e notas',
  'evidence.gallery.description':
    '{shown} de {total} registros capturados correspondem aos filtros acima.',
  'evidence.gallery.empty.title': 'Nenhuma evidência corresponde a estes filtros',
  'evidence.gallery.empty.description':
    'Nada foi capturado para este empreendimento, etapa e situação em conjunto.',
  'evidence.gallery.showing': 'Exibindo {shown} de {total} itens de evidência correspondentes.',
  'evidence.gallery.showFewer': 'Mostrar menos',
  'evidence.gallery.showAll': 'Mostrar todos os {total}',
  'evidence.capturedBy': 'capturado por {name}',
  'evidence.withdraw': 'Retirar',
  'evidence.publish': 'Publicar',
  'evidence.withdrawAria': 'Retirar {caption} do portal do investidor',
  'evidence.publishAria': 'Publicar {caption} no portal do investidor',
  'evidence.toast.released': 'Liberado para investidores',
  'evidence.toast.withdrawn': 'Retirado do portal',
  'evidence.toast.description': '{caption} — {phase}.',
  'evidence.noPhase': 'Sem etapa',
  'evidence.documents.title': 'Documentos',
  'evidence.documents.description':
    'Cada nota fiscal, recibo, contrato, alvará, orçamento, relatório e vistoria arquivado. Verificação e liberação são colunas separadas de propósito — a tabela pode mostrar um documento verificado que ainda não foi liberado.',
  'evidence.documents.caption': 'Documentos arquivados, com situação de verificação e de liberação',
  'evidence.documents.emptyTitle': 'Nenhum documento corresponde aos filtros atuais',
  'evidence.documents.emptyDescription':
    'Limpe os filtros de empreendimento, etapa ou publicação acima para ver o registro completo.',
  'evidence.col.document': 'Documento',
  'evidence.col.project': 'Empreendimento',
  'evidence.col.phase': 'Etapa',
  'evidence.col.type': 'Tipo',
  'evidence.col.verification': 'Verificação',
  'evidence.col.release': 'Liberação',
  'evidence.col.size': 'Tamanho',
  'evidence.col.uploadedBy': 'Enviado por',
  'project.evidence.metric.captured': 'Fotos e notas capturadas',
  'project.evidence.metric.released': 'Liberados para investidores',
  'project.evidence.metric.releasedHint': 'Visíveis no portal hoje.',
  'project.evidence.metric.awaiting': 'Aguardando publicação',
  'project.evidence.metric.awaitingHint': 'Capturados internamente e retidos no portal.',
  'project.evidence.metric.documents': 'Documentos arquivados',
  'project.evidence.metric.documentsHint': '{unreleased} sem liberação.',
  'project.evidence.capturedBy': 'capturado por {name}',
  'project.evidence.released': 'Liberado',
  'project.evidence.notReleased': 'Não liberado',
  'project.evidence.stagedNotUploaded': 'preparado, não enviado',
  'project.evidence.removeStaged': 'Remover {name} dos arquivos preparados',
  'project.evidence.phase.released': '{released} de {total} liberados',
  'project.evidence.phase.documents.one': '{count} documento',
  'project.evidence.phase.documents.other': '{count} documentos',
  'project.evidence.phase.staged': '{count} preparados',
  'project.evidence.phase.counts':
    '{captured} capturados · {released} liberados · {awaiting} aguardando publicação',
  'project.evidence.progress': '{phase} — evidências liberadas para investidores',
  'project.evidence.photosAndNotes': 'Fotos e notas',
  'project.evidence.documentsHeading': 'Documentos',
  'project.evidence.stagedHeading': 'Preparados nesta sessão',
  'project.evidence.list.evidence': 'evidências de {phase}',
  'project.evidence.list.documents': 'documentos de {phase}',
  'project.evidence.list.staged': 'arquivos preparados de {phase}',
  'project.evidence.empty.title': 'Nada capturado nesta etapa ainda',
  'project.evidence.empty.description':
    'Fotos, vídeos, notas e documentos podem ser vinculados a uma etapa assim que a obra começa — o §14 exige apenas que estejam vinculados à etapa correta antes de serem publicados.',
  'project.evidence.attachFiles': 'Vincular arquivos',
  'project.evidence.toast.staged.one': '{count} arquivo preparado',
  'project.evidence.toast.staged.other': '{count} arquivos preparados',
  'project.evidence.toast.stagedBody':
    'Vinculados a {phase}. Nada fica visível para o investidor antes da liberação.',
  'project.evidence.upload.title': 'Adicionar evidência',
  'project.evidence.upload.description':
    'Fotos, vídeos, documentos e notas entram na primeira liberação — cada um é vinculado a uma etapa antes de poder ser publicado.',
  'project.evidence.upload.drop': 'Solte fotos, vídeos ou documentos aqui.',
  'project.evidence.upload.choose': 'Escolher arquivos',
  'project.evidence.upload.attachTo': 'Vincular à etapa',
  'project.evidence.upload.captionLabel': 'Legenda deste lote',
  'project.evidence.upload.captionDescription':
    'Usada como legenda quando os arquivos são preparados. Opcional — caso contrário, o nome do arquivo é usado.',
  'project.evidence.upload.ready': 'Prontos para envio',
  'project.evidence.nothingStaged': 'Nada preparado ainda.',
  'project.evidence.readyFor.one': '{count} arquivo pronto para {phase}',
  'project.evidence.readyFor.other': '{count} arquivos prontos para {phase}',
  'project.evidence.clear': 'Limpar',
  'project.evidence.stage.one': 'Preparar {count} arquivo',
  'project.evidence.stage.other': 'Preparar {count} arquivos',
  'project.evidence.staging.title': 'Preparar não é publicar',
  'project.evidence.staging.description':
    'Os arquivos adicionados aqui ficam vinculados à etapa e permanecem internos. O investidor só os vê depois que a atualização que os carrega passar por revisão e liberação.',
  'project.evidence.byPhase.title': 'Evidências por etapa',
  'project.evidence.byPhase.description':
    'O que foi capturado, o que foi liberado e os documentos arquivados em cada etapa.',

  /* ---- Financials and suppliers (GC) ---- */
  'financials.title': 'Financeiro',
  'financials.subtitle':
    'Orçado contra realizado, e para onde o dinheiro foi, nos três empreendimentos.',
  'financials.metrics.contributed': 'Aportes realizados',
  'financials.metrics.contributedHint':
    'Nos três empreendimentos — capital dos investidores efetivamente integralizado.',
  'financials.metrics.spent': 'Custo incorrido até o momento',
  'financials.metrics.spentHint': 'Nos três empreendimentos — lançado no razão de despesas.',
  'financials.metrics.unspent': 'Saldo não utilizado',
  'financials.metrics.unspentHint':
    'Nos três empreendimentos — capital comprometido menos o que foi efetivamente gasto.',
  'financials.metrics.variance': 'Variação em relação ao orçamento',
  'financials.metrics.varianceHint':
    'Nos três empreendimentos — custo incorrido contra o orçamento completo por etapas, que inclui etapas ainda não iniciadas.',
  'financials.chart.title': 'Orçado contra realizado — Lakeview Residence',
  'financials.chart.description':
    'A série do gráfico é o empreendimento Lakeview: orçado contra realizado por mês e por etapa da obra. Os números do topo e o razão abaixo cobrem os três empreendimentos.',
  'financials.cash.title': 'Fluxo de caixa',
  'financials.cash.description':
    'Aportes contra gastos, mês a mês, com o resultado líquido acumulado e a posição final.',
  'financials.cash.caption':
    'Fluxo de caixa mensal — aportes contra gastos, com o movimento líquido e a posição acumulada.',
  'financials.cash.month': 'Mês',
  'financials.cash.contributed': 'Aportes',
  'financials.cash.spent': 'Gasto',
  'financials.cash.net': 'Líquido',
  'financials.cash.cumulative': 'Acumulado',
  'financials.ledger.title': 'Razão de despesas',
  'financials.ledger.intro.one':
    'Toda despesa lançada nos três empreendimentos — {count} linha. O subtotal sob cada coluna de valor cobre todo o conjunto filtrado, não a página na tela, então uma visão filtrada fecha sem precisar ser exportada.',
  'financials.ledger.intro.other':
    'Toda despesa lançada nos três empreendimentos — {count} linhas. O subtotal sob cada coluna de valor cobre todo o conjunto filtrado, não a página na tela, então uma visão filtrada fecha sem precisar ser exportada.',
  'financials.ledger.caption': 'Despesas de todos os empreendimentos',
  'financials.ledger.emptyTitle': 'Nenhuma despesa corresponde a esses filtros',
  'financials.ledger.emptyDescription': 'Amplie um filtro ou limpe todos e comece de novo.',
  'financials.ledger.errorTitle': 'Falha ao carregar o razão de despesas',
  'financials.variance.title': 'Como ler a coluna de variação',
  'financials.variance.body':
    'Previsto é o valor orçado para a etapa, e a variação é o valor menos o previsto. Uma linha negativa ficou abaixo do previsto, uma positiva ficou acima — então as linhas que mexeram no orçamento são as mais distantes de zero.',
  'financials.column.ref': 'Ref.',
  'financials.column.development': 'Empreendimento',
  'financials.column.phase': 'Etapa',
  'financials.column.category': 'Categoria',
  'financials.column.vendor': 'Fornecedor',
  'financials.column.forecast': 'Previsto',
  'financials.column.variance': 'Variação',
  'financials.column.delivery': 'Entrega',
  'financials.column.description': 'Descrição',
  'financials.search.developments': 'Buscar empreendimentos…',
  'financials.search.phases': 'Buscar etapas…',
  'financials.search.categories': 'Buscar categorias…',
  'financials.search.vendors': 'Buscar fornecedores…',
  'financials.search.statuses': 'Buscar situações…',
  'financials.search.delivery': 'Buscar entregas…',
  'financials.search.deliveryStates': 'Buscar estados de entrega…',
  'financials.expenseStatus.approved': 'Aprovada',
  'financials.expenseStatus.inReview': 'Em análise',
  'financials.expenseStatus.disputed': 'Contestada',
  'financials.delivery.delivered': 'Entregue',
  'financials.delivery.partial': 'Parcial',
  'financials.delivery.pending': 'Pendente',
  'financials.delivery.notApplicable': 'Não aplicável',
  'project.financials.notFoundMessage':
    'Nada corresponde a "{id}". Ele pode ter sido encerrado ou arquivado.',
  'project.financials.phases.title': 'Orçamento por etapa',
  'project.financials.phases.description':
    'Os realizados abaixo somam os {amount} de custo incorrido até o momento mostrados acima — as linhas de etapa são esse valor decomposto.',
  'project.financials.phases.caption':
    'Orçado, realizado, variação e orçamento consumido em cada etapa de {name}.',
  'project.financials.column.budgeted': 'Orçado',
  'project.financials.column.actual': 'Realizado',
  'project.financials.column.used': 'Consumido',
  'project.financials.phaseCount.one': '{count} etapa',
  'project.financials.phaseCount.other': '{count} etapas',
  'project.financials.budgetConsumed': '{name} — orçamento consumido',
  'project.financials.unverified.title.one':
    '{count} linha de despesa está lançada, mas ainda não verificada',
  'project.financials.unverified.title.other':
    '{count} linhas de despesa estão lançadas, mas ainda não verificadas',
  'project.financials.unverified.description':
    'Elas somam {amount}. O dinheiro está comprometido e entra nos números acima, mas a verificação não foi encerrada, então os totais das etapas ainda podem mudar.',
  'project.financials.aheadOfDelivery.title.one':
    '{count} linha de despesa foi paga antes da entrega',
  'project.financials.aheadOfDelivery.title.other':
    '{count} linhas de despesa foram pagas antes da entrega',
  'project.financials.aheadOfDelivery.description':
    'Elas somam {amount}. Nessas linhas o pagamento corre na frente do serviço, então o custo acima ainda não tem respaldo no que está na obra.',
  'project.financials.disputed.title.one': '{count} linha de despesa está contestada',
  'project.financials.disputed.title.other': '{count} linhas de despesa estão contestadas',
  'project.financials.disputed.description':
    'Elas somam {amount}. Não estão aprovadas nem liquidadas, e o custo acima deve ser lido como sob contestação enquanto assim estiverem.',
  'project.financials.ledger.description':
    'Toda linha lançada por trás dos realizados das etapas. Filtre ou ordene e o subtotal do rodapé acompanha — ele cobre todo o conjunto filtrado, não a página em exibição.',
  'project.financials.ledger.caption': 'Razão de despesas de {name}',
  'project.financials.ledger.emptyTitle': 'Nenhuma linha de despesa corresponde a esses filtros',
  'project.financials.funding.title': 'Aportes por investidor',
  'project.financials.funding.description':
    'Comprometido é o capital que cada participação subscreveu — um número diferente dos {amount} de aportes realizados, que é o que o bloco "Aportes" no topo desta página mostra.',
  'project.financials.funding.caption':
    'Os investidores que financiam {name}, com participação, capital comprometido e aportes realizados, e as chamadas de capital emitidas e liquidadas.',
  'project.financials.column.investor': 'Investidor',
  'project.financials.column.share': 'Participação',
  'project.financials.column.committed': 'Comprometido',
  'project.financials.column.contributed': 'Aportes',
  'project.financials.column.callsIssued': 'Chamadas emitidas',
  'project.financials.column.callsSettled': 'Chamadas liquidadas',
  'project.financials.investorCount.one': '{count} investidor',
  'project.financials.investorCount.other': '{count} investidores',
  'contractors.title': 'Fornecedores',
  'contractors.subtitle':
    'Quem está no cadastro, o que foi contratado, o que já foi pago e o que o razão de despesas registrou de fato para cada fornecedor.',
  'contractors.suppliersOnFile': 'Fornecedores cadastrados',
  'contractors.suppliersOnFileHint': 'Toda empreiteira e todo parceiro do cadastro.',
  'contractors.contractValue': 'Valor do contrato',
  'contractors.contractValueHint': 'Soma do valor contratado de todo o cadastro.',
  'contractors.paidToDate': 'Pago até o momento',
  'contractors.paidToDateHint': 'Soma do que já foi pago sobre esses contratos.',
  'contractors.recordedSpend': 'Custo registrado',
  'contractors.recordedSpendHint':
    'Toda linha de despesa do razão, atribuída a um fornecedor pelo id do fornecedor.',
  'contractors.lookup.title': 'Consulta de fornecedor',
  'contractors.lookup.description':
    'A busca roda no lado da API: o campo envia o termo para a fonte remota e mostra o que volta.',
  'contractors.supplier': 'Fornecedor',
  'contractors.lookup.placeholder': 'Buscar fornecedores por nome ou especialidade…',
  'contractors.lookup.hint':
    'Compara com o nome do fornecedor e sua especialidade — nenhum filtro acontece no navegador.',
  'contractors.detail.description':
    'O contrato como o cadastro o registra, e as linhas de despesa que o razão atribui a este fornecedor pelo id do fornecedor.',
  'contractors.contractId': 'ID do contrato',
  'contractors.outstanding': 'Saldo a pagar',
  'contractors.crewSize': 'Tamanho da equipe',
  'contractors.started': 'Início',
  'contractors.lastInspection': 'Última vistoria',
  'contractors.approvedAt': 'Aprovado em',
  'contractors.verified': 'Verificado',
  'contractors.yes': 'Sim',
  'contractors.no': 'Não',
  'contractors.progress': '{name} — conclusão',
  'contractors.noSpend':
    'Nenhuma linha de despesa desta versão corresponde a {name}. O contrato está no cadastro, mas nada foi registrado contra ele no razão.',
  'contractors.spend.caption.one':
    'Linhas de despesa atribuídas a {name} — {count} linha, {amount} no total.',
  'contractors.spend.caption.other':
    'Linhas de despesa atribuídas a {name} — {count} linhas, {amount} no total.',
  'contractors.column.project': 'Empreendimento',
  'contractors.phase': 'Etapa',
  'contractors.totalRecorded': 'Total registrado',
  'contractors.empty.title': 'Nenhum fornecedor selecionado',
  'contractors.empty.description':
    'Busque no cadastro acima para ver o contrato de um fornecedor, os pagamentos registrados nele e as linhas de despesa que o razão atribui a ele.',
  'contractors.register.title': 'Cadastro de fornecedores',
  'contractors.register.description':
    'A ordenação, os filtros, a paginação e os subtotais do rodapé rodam na fonte de dados, e não no navegador, então cada subtotal cobre todo o conjunto filtrado e não apenas a página na tela.',
  'contractors.register.caption': 'Fornecedores — contrato, custo e vistorias',
  'contractors.register.emptyTitle': 'Nenhum fornecedor para mostrar',
  'contractors.register.errorTitle': 'Falha ao carregar o cadastro de fornecedores',
  'contractors.register.searchTrades': 'Buscar especialidades…',
  'contractors.register.note':
    'O registro de fornecedor desta versão não tem cidade, contato nem documentos, então o cadastro mostra os dados de contrato, custo e vistoria que existem.',
  'contractors.crew': 'Equipe',
  'contractors.completion': 'Conclusão',
  'contractors.trade': 'Especialidade',

  /* ---- Investors and permissions (GD) ---- */
  'investors.title': 'Investidores',
  'investors.intro':
    'Quem tem posição na carteira, o que comprometeu e o que já foi efetivamente chamado. Uma posição é uma participação — o cadastro guarda a identidade, os empreendimentos guardam o dinheiro.',
  'investors.onRegister': 'No cadastro',
  'investors.prospectCount.one': '{count} prospecto',
  'investors.prospectCount.other': '{count} prospectos',
  'investors.closedCount.one': '{count} encerrado',
  'investors.closedCount.other': '{count} encerrados',
  'investors.committedHint': 'Soma de todas as participações, em todos os empreendimentos.',
  'investors.contributedToDate': 'Aportes realizados até o momento',
  'investors.outstandingHint': '{amount} ainda em aberto nos cronogramas de chamada de capital.',
  'investors.register': 'Cadastro de investidores',
  'investors.registerHint':
    'Abra um investidor para ver as participações, os documentos e o que ele já leu.',
  'investors.kind': 'Tipo',
  'investors.kindFilter': 'Filtrar por tipo de investidor',
  'investors.statusFilter': 'Filtrar por situação',
  'investors.city': 'Cidade',
  'investors.cityFilter': 'Filtrar por cidade',
  'investors.allCities': 'Todas as cidades',
  'investors.searchLabel': 'Buscar no cadastro de investidores',
  'investors.searchPlaceholder': 'Nome, e-mail ou identificação fiscal',
  'investors.tableCaption':
    'O cadastro de investidores, com os empreendimentos e o capital comprometido e aportado derivados das participações de cada investidor.',
  'investors.colInvestor': 'Investidor',
  'investors.colJoined': 'Entrada',
  'investors.colProjects': 'Empreendimentos',
  'investors.colCommitted': 'Comprometido',
  'investors.colShare': 'Participação',
  'investors.emptyTitle': 'Nenhum investidor corresponde',
  'investors.emptyDescription': 'Nenhum investidor do cadastro corresponde aos filtros aplicados.',
  'investors.subtotal': 'Subtotal',
  'investors.filteredTotal': 'Total filtrado — {shown} de {total}',
  'investor.status.active': 'Investidor ativo',
  'investor.kind.fund': 'Fundo de investimento',
  'investor.notFoundTitle': 'Investidor não encontrado',
  'investor.notFoundHeading': 'Investidor inexistente',
  'investor.notFoundMessage': 'Nada no cadastro corresponde a "{id}". Ele pode ter sido arquivado.',
  'investor.joined': 'Entrada em {date}',
  'investor.email': 'E-mail',
  'investor.phone': 'Telefone',
  'investor.city': 'Cidade',
  'investor.taxId': 'Identificação fiscal',
  'investor.developments': 'Empreendimentos',
  'investor.committed': 'Comprometido',
  'investor.outstanding': 'Em aberto',
  'investor.callsSettled.one': '{settled} de {count} chamada de capital quitada.',
  'investor.callsSettled.other': '{settled} de {count} chamadas de capital quitadas.',
  'investor.participations': 'Participações',
  'investor.participationsHint':
    'Uma linha por empreendimento em que este investidor tem participação.',
  'investor.emptyTitle': 'Nenhuma participação registrada',
  'investor.emptyPipeline':
    '{name} está no cadastro como prospecto, com um registro de participação zero em {projects}. Uma participação sem fatia não confere posição e não entra em nenhum total.',
  'investor.emptyNone': '{name} não tem participação em nenhum empreendimento da carteira.',
  'investor.tableCaption':
    'As participações deste investidor, com a fatia, o capital e as chamadas de capital de cada empreendimento.',
  'investor.col.development': 'Empreendimento',
  'investor.col.joined': 'Entrada',
  'investor.col.share': 'Participação',
  'investor.col.committed': 'Comprometido',
  'investor.col.outstanding': 'Em aberto',
  'investor.col.calls': 'Chamadas',
  'investor.col.documents': 'Documentos',
  'investor.totalAcross.one': 'Total em {count} empreendimento',
  'investor.totalAcross.other': 'Total em {count} empreendimentos',
  'investor.documentsPanel': 'Documentos arquivados',
  'investor.documentsPanelHint':
    'O que o escritório mantém vinculado às posições deste investidor.',
  'investor.noDocuments': 'Nada está arquivado contra uma posição que não existe.',
  'investor.documentKinds': 'Contrato de subscrição, avisos de chamada de capital e extratos',
  'investor.documentsTotal.one':
    '{count} documento no total. Os arquivos ficam vinculados ao empreendimento, porque um contrato de subscrição pertence à posição, e não à pessoa.',
  'investor.documentsTotal.other':
    '{count} documentos no total. Os arquivos ficam vinculados ao empreendimento, porque um contrato de subscrição pertence à posição, e não à pessoa.',
  'investor.readPanel': 'O que este investidor já leu',
  'investor.readPanelHint.one': '{opened} de {count} atualização publicada aberta.',
  'investor.readPanelHint.other': '{opened} de {count} atualizações publicadas abertas.',
  'investor.readershipTitle': 'Recurso proposto — registro de leitura, pendente de validação',
  'investor.readershipBody':
    'A plataforma ainda não registra quem abriu uma atualização publicada. O que segue é o log de exemplo usado no exercício de validação (RF11, marcado como pendente). Ele mostra o que a tela fará quando o registro for acordado e ativado — não é um comprovante de entrega.',
  'investor.nothingPublished':
    'Nada foi publicado para {name} nos empreendimentos dele até o momento.',
  'investor.publishedOn': 'publicado em {date}',
  'investor.opened': 'Aberto em {date}',
  'investor.notOpened': 'Não aberto',
  'investor.readProgress': 'Leu {percent} desta atualização',
  'project.investors.reconciles': 'O quadro societário fecha',
  'project.investors.mismatch': 'O quadro societário não fecha',
  'project.investors.reconcilesBody':
    'Os aportes dos investidores, de {contributed}, conferem exatamente com os {committed} de capital comprometido no registro do empreendimento, e os {investorShare} detidos pelos investidores mais os {builderShare} retidos pela construtora são a totalidade do empreendimento.',
  'project.investors.mismatchBody':
    'Os aportes dos investidores, de {contributed}, estão {difference} distantes do registro do empreendimento, e as participações somam {shareSum}, quando o esperado é {expected}. Isto é um defeito nos dados e os números abaixo não devem ser publicados.',
  'project.investors.capTable': 'Quadro societário',
  'project.investors.capTableHint':
    'Quem detém {name} e o que cada um comprometeu e já integralizou.',
  'project.investors.emptyTitle': 'Nenhum investidor tem participação ainda',
  'project.investors.emptyBody':
    '{name} é financiado integralmente pela construtora neste momento. A primeira participação com fatia societária aparece aqui.',
  'project.investors.tableCaption':
    'Os investidores de {name}, com participação, capital comprometido e aportes realizados, saldo em aberto e chamadas de capital.',
  'project.investors.col.participant': 'Participante',
  'project.investors.col.joined': 'Entrada',
  'project.investors.col.share': 'Participação',
  'project.investors.col.committed': 'Comprometido',
  'project.investors.col.outstanding': 'Em aberto',
  'project.investors.col.callsSettled': 'Chamadas quitadas',
  'project.investors.col.documents': 'Documentos',
  'project.investors.count.one': '{count} investidor',
  'project.investors.count.other': '{count} investidores',
  'project.investors.retained': 'Retido pela construtora',
  'project.investors.retainedNote':
    'Fora do quadro societário: a fatia da própria construtora em {name} não tem participação nem chamada de capital.',
  'project.investors.pipelineTitle': 'No pipeline, sem fatia',
  'project.investors.pipelineHint':
    'Registrado neste empreendimento para que a conversa não se perca, fora de todos os números acima.',
  'project.investors.entered': 'Entrada em {date}',
  'project.investors.documentsShared.one': '{count} documento compartilhado',
  'project.investors.documentsShared.other': '{count} documentos compartilhados',
  'project.investors.shareBadge': '{share} de participação',
  'project.investors.pipelineNote':
    'Uma participação com fatia zero é um prospecto, não um detentor. Ela fica fora do quadro societário, do total de participação e de todos os números de capital desta tela — uma participação no sistema não é uma participação no empreendimento.',
  'permissions.title': 'Permissões',
  'permissions.intro':
    'Cinco perfis e o que cada um pode alcançar. Este é o modelo que a plataforma aplica — a matriz abaixo é ele por inteiro.',
  'permissions.metric.profiles': 'Perfis',
  'permissions.metric.mapped': 'Membros da equipe mapeados',
  'permissions.metric.mappedHint.one': '{count} pessoa na equipe de exemplo.',
  'permissions.metric.mappedHint.other': '{count} pessoas na equipe de exemplo.',
  'permissions.metric.mayRelease': 'Podem aprovar uma liberação',
  'permissions.metric.mayReleaseHint': 'Perfis cuja coluna de publicação está marcada como Sim.',
  'permissions.metric.portal': 'Com acesso ao portal',
  'permissions.metric.portalHint': 'Somente o perfil de investidor pode entrar no portal.',
  'permissions.matrixTitle': 'Matriz de permissões',
  'permissions.matrixHint': 'Selecione um perfil para ler o que ele pode e o que não pode fazer.',
  'permissions.matrixCaption':
    'Matriz de permissões: cada perfil em relação a escopo de empreendimentos, conteúdo de campo, acesso financeiro, publicação e acesso ao portal',
  'permissions.matrixNote':
    'Sim e Não são absolutos. Qualquer outra redação é a condição que o documento estabelece para aquele perfil — uma expressão como "conforme a autoridade" é um limite que precisa ser concedido, não um padrão.',
  'permissions.col.profile': 'Perfil',
  'permissions.col.projects': 'Empreendimentos',
  'permissions.col.field': 'Conteúdo de campo',
  'permissions.col.financial': 'Financeiro',
  'permissions.col.publishing': 'Publicação',
  'permissions.col.portal': 'Portal do investidor',
  'permissions.can': 'Pode',
  'permissions.cannot': 'Não pode',
  'permissions.whoHolds': 'Quem tem este perfil',
  'permissions.holdersLabel': 'Pessoas com o perfil {name}',
  'permissions.noHolders':
    'Ninguém na equipe de exemplo está mapeado a este perfil. Nesta demonstração, é a conta conectada à plataforma — o proprietário da carteira.',
  'permissions.portalNote':
    'O portal é um produto separado, com as suas próprias quatro seções. Nenhuma tela interna é alcançável a partir dele — é isso que a coluna de portal deste perfil garante.',
  'permissions.rulesTitle': 'As regras por trás do modelo',
  'permissions.rulesHint':
    'Os seis controles que a §14 pede, nas palavras com que a plataforma os aplica.',
  'permissions.cell.yes': 'Sim',
  'permissions.cell.no': 'Não',
  'permissions.cell.onlyAssignedProjects': 'Somente empreendimentos atribuídos',
  'permissions.cell.createMedia': 'Criar fotos, vídeos e notas',
  'permissions.cell.managedProjects': 'Empreendimentos que gerencia',
  'permissions.cell.consultConsolidate': 'Consultar e consolidar',
  'permissions.cell.perAuthority': 'Conforme a autoridade',
  'permissions.cell.mayPrepare': 'Pode preparar; aprovação conforme a regra final',
  'permissions.cell.authorisedProjects': 'Empreendimentos autorizados',
  'permissions.cell.consultWhenNeeded': 'Consultar quando necessário',
  'permissions.cell.createConsultVerify': 'Criar, consultar e verificar conforme a autoridade',
  'permissions.cell.notNecessarily': 'Não necessariamente',
  'permissions.cell.broad': 'Amplo',
  'permissions.cell.onlyTheirParticipations': 'Somente as próprias participações',
  'permissions.cell.publishedContentOnly': 'Somente conteúdo publicado',
  'permissions.cell.releasedFinancialsOnly': 'Somente dados financeiros liberados',
  'permissions.profile.fieldLead': 'Encarregado de obra',
  'permissions.profile.fieldLeadSummary':
    'Na obra, registrando o que aconteceu. Não vê valores e não libera nada.',
  'permissions.profile.manager': 'Gerente / coordenador',
  'permissions.profile.managerSummary':
    'Conduz os empreendimentos que gerencia e consolida o que o campo envia.',
  'permissions.profile.finance': 'Financeiro / administrativo',
  'permissions.profile.financeSummary':
    'Responde pela parte financeira: cria, consulta e verifica — mas a publicação não é ato seu.',
  'permissions.profile.owner': 'Proprietário / sócio',
  'permissions.profile.ownerSummary':
    'Vê a carteira inteira e é o perfil em que recai a aprovação final da publicação.',
  'permissions.profile.investorSummary':
    'O portal, e somente o portal: as próprias participações, e apenas o que foi liberado.',
  'permissions.fieldLead.can1':
    'Capturar fotos, vídeos e notas nos empreendimentos aos quais está atribuído',
  'permissions.fieldLead.can2': 'Vincular cada captura à etapa a que pertence',
  'permissions.fieldLead.cannot1': 'Abrir um empreendimento ao qual não está atribuído',
  'permissions.fieldLead.cannot2': 'Ver qualquer valor financeiro',
  'permissions.fieldLead.cannot3': 'Preparar, aprovar ou liberar uma publicação',
  'permissions.fieldLead.cannot4': 'Entrar no portal do investidor',
  'permissions.manager.can1':
    'Consultar e consolidar as evidências capturadas nos seus empreendimentos',
  'permissions.manager.can2': 'Preparar uma publicação para revisão',
  'permissions.manager.can3': 'Ver o detalhe financeiro dentro da autoridade que lhe foi concedida',
  'permissions.manager.cannot1': 'Aprovar a liberação final de uma publicação',
  'permissions.manager.cannot2': 'Alcançar empreendimentos fora da sua carteira',
  'permissions.manager.cannot3': 'Entrar no portal do investidor',
  'permissions.finance.can1':
    'Criar, consultar e verificar registros financeiros dentro da sua autoridade',
  'permissions.finance.can2':
    'Abrir as evidências de um empreendimento autorizado quando o financeiro precisar',
  'permissions.finance.cannot1': 'Alcançar um empreendimento para o qual não tem autorização',
  'permissions.finance.cannot2': 'Tratar uma verificação como publicação — são dois atos distintos',
  'permissions.finance.cannot3': 'Entrar no portal do investidor',
  'permissions.owner.can1': 'Abrir todos os empreendimentos da carteira',
  'permissions.owner.can2':
    'Criar, consultar e verificar tanto conteúdo de campo quanto dados financeiros',
  'permissions.owner.can3': 'Aprovar e liberar uma publicação para os investidores',
  'permissions.owner.cannot1':
    'Entrar no portal do investidor — essa é a superfície do próprio investidor',
  'permissions.investor.can1': 'Abrir os empreendimentos nos quais tem participação',
  'permissions.investor.can2':
    'Ler atualizações publicadas, dados financeiros liberados e documentos liberados',
  'permissions.investor.can3': 'Ver o pipeline de oportunidades aberto aos investidores',
  'permissions.investor.cannot1':
    'Ver evidências internas, rascunhos ou qualquer documento que não tenha sido liberado',
  'permissions.investor.cannot2': 'Ver a participação de outro investidor',
  'permissions.investor.cannot3': 'Criar ou aprovar qualquer coisa',
  'permissions.rule.byProject': 'Acesso por empreendimento',
  'permissions.rule.byProjectBody':
    'Um perfil só abre os empreendimentos aos quais está vinculado. O escopo é aplicado na requisição, não escondendo um link — um encarregado de obra não alcança um empreendimento que não está na sua lista, e um gerente não alcança um empreendimento fora da sua carteira.',
  'permissions.rule.byRole': 'Acesso por perfil',
  'permissions.rule.byRoleBody':
    'Dentro de um empreendimento, o que um perfil pode fazer vem do seu papel: capturar, consolidar, verificar ou aprovar. A matriz acima é o modelo inteiro — não há um segundo conjunto de regras aplicado no código que o cliente não possa ver.',
  'permissions.rule.publication': 'Separação da publicação',
  'permissions.rule.publicationBody':
    'Preparar, aprovar e liberar são três atos distintos. Uma atualização é registrada, revisada, aprovada e só então publicada, e quem a prepara não precisa ser quem a aprova. O portal mostra o subconjunto publicado — nada chega ao investidor apenas por ter sido cadastrado.',
  'permissions.rule.investor': 'Investidor restrito',
  'permissions.rule.investorBody':
    'O investidor vê as próprias participações, o conteúdo publicado e os dados financeiros liberados — nada além disso. O portal dele é um produto separado, com navegação própria, então nenhuma tela interna é alcançada a partir dele por acidente.',
  'permissions.rule.traceability': 'Rastreabilidade',
  'permissions.rule.traceabilityBody':
    'Toda liberação registra quem preparou, quem revisou, quem aprovou e quando. Todo acesso de investidor a uma atualização publicada é registrado, com quanto dela foi lido.',
  'permissions.rule.verification': 'Verificação de evidências',
  'permissions.rule.verificationBody':
    'Um documento tem dois estados independentes: se foi verificado internamente e se foi liberado aos investidores. Os dois são necessários para que ele apareça no portal — por isso um documento verificado ainda pode estar invisível para o investidor.',
  'permissions.verificationUnreleased.one':
    'Na base hoje: {count} documento verificado não liberado aos investidores',
  'permissions.verificationUnreleased.other':
    'Na base hoje: {count} documentos verificados não liberados aos investidores',
  'permissions.verificationReleased.one': '{count} liberado',
  'permissions.verificationReleased.other': '{count} liberados',
  'permissions.accessLogCaption': 'Acesso de investidores a atualizações publicadas',
  'permissions.access.investor': 'Investidor',
  'permissions.access.development': 'Empreendimento',
  'permissions.access.update': 'Atualização',
  'permissions.access.viewed': 'Visualizado em',
  'permissions.access.read': 'Lido',

  /* ---- Publications, reports and notifications (GE) ---- */
  'publications.title': 'Publicações',
  'publications.intro':
    'Tudo o que a plataforma pretende comunicar aos investidores, e o quanto cada registro já avançou até ser comunicado. Registrar não é publicar: só a quarta situação fica visível do outro lado.',
  'publications.gate.draft':
    'Uso interno. Nenhum investidor pode vê-la, e nenhum investidor pode ser avisado sobre ela.',
  'publications.gate.inReview':
    'Registrada e em conferência. Ainda invisível para os investidores.',
  'publications.gate.approved':
    'Liberada para publicação, mas não publicada. Aprovação não é publicação.',
  'publications.gate.published': 'Visível para todo investidor com participação no empreendimento.',
  'publications.queue': 'Fila',
  'publications.filterLabel': 'Filtrar a fila de publicação por situação',
  'publications.filterAll': 'Todas as {count}',
  'publications.filterStatus': '{status} {count}',
  'publications.visibleSummary':
    '{visible} de {total} registros estão visíveis para os investidores.',
  'publications.emptyTitle': 'Nada nesta situação',
  'publications.emptyBody': 'Nenhuma publicação na fila está na situação que você filtrou.',
  'publications.badgeNotified': 'Investidores notificados',
  'publications.badgeQuiet': 'Liberada sem aviso',
  'publications.reaches.one': 'Alcança {count} investidor neste empreendimento.',
  'publications.reaches.other': 'Alcança {count} investidores neste empreendimento.',
  'publications.sendForReview': 'Enviar para revisão',
  'publications.requestChanges': 'Solicitar ajustes',
  'publications.approve': 'Aprovar',
  'publications.withdrawApproval': 'Retirar aprovação',
  'publications.publishToInvestors': 'Publicar para os investidores',
  'publications.releasedNote':
    'Liberada em {date} — o registro está encerrado e não pode mais ser editado.',
  'publications.stepPrepared': 'Preparada',
  'publications.stepReviewed': 'Revisada',
  'publications.awaitingReviewer': 'Aguardando revisor',
  'publications.awaitingApproval': 'Aguardando aprovação',
  'publications.notReleased': 'Não liberada',
  'publications.progressLabel': 'Progresso da publicação de {title}',
  'publications.photographs': 'Fotografias',
  'publications.documents': 'Documentos',
  'publications.spendToDate': 'Gasto até o momento',
  'publications.varianceVsBudget': 'Variação sobre o orçamento',
  'publications.under': 'abaixo',
  'publications.over': 'acima',
  'publications.notifyLabel': 'Notificar os investidores quando isto for liberado',
  'publications.hintNotified': 'Os investidores foram notificados quando ela saiu.',
  'publications.hintQuiet': 'Ela saiu sem nenhuma notificação.',
  'publications.hintWillNotify': 'Uma notificação será disparada junto com a liberação.',
  'publications.hintWillBeQuiet':
    'Nenhuma notificação será disparada — o §13 permite uma liberação silenciosa.',
  'publications.notifiedTitle': 'Os investidores serão notificados',
  'publications.silentSetTitle': 'Liberação silenciosa definida',
  'publications.toggleOnBody': '{title} vai disparar uma notificação quando for publicada.',
  'publications.toggleOffBody': '{title} será publicada sem notificar ninguém.',
  'publications.toast.sentTitle': 'Enviada para revisão',
  'publications.toast.sentBody':
    '{title} está com o revisor. Continua invisível para os investidores.',
  'publications.toast.approvedBody':
    '{title} está liberada para publicação. Ela só fica visível quando for publicada.',
  'publications.toast.backToDraftTitle': 'De volta a rascunho',
  'publications.toast.backToDraftBody':
    '{title} voltou para o autor. A aprovação do revisor foi cancelada.',
  'publications.toast.withdrawnTitle': 'Aprovação retirada',
  'publications.toast.withdrawnBody':
    '{title} voltou para revisão. A aprovação que ela tinha não vale mais.',
  'publications.toast.publishedNotifiedTitle': 'Publicada e notificada',
  'publications.toast.publishedQuietlyTitle': 'Publicada em silêncio',
  'publications.toast.notifiedBody.one': '{count} investidor em {project} foi notificado.',
  'publications.toast.notifiedBody.other': '{count} investidores em {project} foram notificados.',
  'publications.toast.quietBody.one':
    'No ar para {count} investidor, sem nenhuma notificação enviada — o §13 permite que uma atualização de rotina saia em silêncio.',
  'publications.toast.quietBody.other':
    'No ar para {count} investidores, sem nenhuma notificação enviada — o §13 permite que uma atualização de rotina saia em silêncio.',
  'publications.confirmTitle': 'Publicar para os investidores?',
  'publications.confirmConfirm': 'Publicar agora',
  'publications.confirmCancel': 'Ainda não',
  'publications.confirmBody.one':
    'fica visível imediatamente para {count} investidor com participação em {project}. A publicação não pode ser desfeita: o registro é encerrado e uma correção precisa sair como uma nova atualização publicada.',
  'publications.confirmBody.other':
    'fica visível imediatamente para {count} investidores com participação em {project}. A publicação não pode ser desfeita: o registro é encerrado e uma correção precisa sair como uma nova atualização publicada.',
  'publications.confirmDiscloses': 'Divulga',
  'publications.confirmSpent': '{amount} gastos',
  'publications.confirmVariance': 'Variação',
  'publications.confirmCarries': 'Traz',
  'publications.countPhotos.one': '{count} foto',
  'publications.countPhotos.other': '{count} fotos',
  'publications.countDocuments.one': '{count} documento',
  'publications.countDocuments.other': '{count} documentos',
  'publications.confirmApprovedBy': 'Aprovado por',
  'publications.confirmApprovedOn': '{name} em {date}',
  'publications.notifiedBody':
    'Uma notificação é disparada junto com a liberação, então ela aparece na fila deles.',
  'publications.quietTitle': 'Isto vai sair em silêncio',
  'publications.quietBody':
    'Nenhuma notificação será disparada. O §13 torna a notificação seletiva de propósito — uma atualização de rotina pode ser publicada sem interromper ninguém. Cancele se a intenção era anunciar.',
  'project.publications.preparedBy': 'Preparado por {name}',
  'project.publications.reviewedBy': 'revisado por {name}',
  'project.publications.approvedBy': 'aprovado por {name} em {date}',
  'project.publications.releasedOn': 'liberado em {date}',
  'project.publications.queueMetric': 'Na fila interna',
  'project.publications.queueHint':
    'Todos os registros, em qualquer situação, podendo ou não ser vistos por um investidor.',
  'project.publications.visibleMetric': 'Visível para os investidores',
  'project.publications.visibleHint': 'De {total} registros. O portal mostra estes e nada mais.',
  'project.publications.hiddenMetric': 'Ainda não visível',
  'project.publications.hiddenHint':
    'Registrado e deliberadamente retido. Estar no sistema não é estar publicado.',
  'project.publications.queueTitle': 'Fila interna',
  'project.publications.queueDescription':
    'Todos os registros deste empreendimento, com o ponto em que cada um parou.',
  'project.publications.queueItemMeta':
    '{photos} · {documents} · divulgaria {amount} ({variance} contra o orçamento)',
  'project.publications.photos.one': '{count} foto',
  'project.publications.photos.other': '{count} fotos',
  'project.publications.documents.one': '{count} documento',
  'project.publications.documents.other': '{count} documentos',
  'project.publications.releasedNotified': 'Liberado e investidores notificados em {date}.',
  'project.publications.releasedQuietly':
    'Liberado em silêncio em {date} — nenhuma notificação foi enviada.',
  'project.publications.stillInternal':
    'Nenhum investidor pode ver isto ainda. Só a situação liberada atravessa a barreira.',
  'project.publications.feedTitle': 'Mural do investidor',
  'project.publications.feedDescription':
    'A lista de atualizações no portal de {project} — apenas o subconjunto liberado.',
  'project.publications.feedEmpty':
    'Nada foi liberado neste empreendimento. Um investidor que abrir o portal hoje não encontra atualização alguma.',
  'project.publications.notificationSent': 'Notificação enviada',
  'project.publications.quietRelease': 'Liberação silenciosa',
  'project.publications.photographs.one': '{count} fotografia',
  'project.publications.photographs.other': '{count} fotografias',
  'project.publications.feedItemMeta': '{photographs} · {documents} · gasto até o momento {amount}',
  'project.publications.audience.one':
    '{count} investidor tem participação neste empreendimento e vê exatamente esta lista. Os {records} registros acima não existem para o portal.',
  'project.publications.audience.other':
    '{count} investidores têm participação neste empreendimento e veem exatamente esta lista. Os {records} registros acima não existem para o portal.',
  'project.publications.notifiedTitle': 'Notificado aos investidores',
  'project.publications.notifiedDescription.one':
    '{count} de {total} marcos deste empreendimento gerou uma notificação.',
  'project.publications.notifiedDescription.other':
    '{count} de {total} marcos deste empreendimento geraram uma notificação.',
  'project.publications.notifiedEmpty':
    'Nenhum marco deste empreendimento gerou notificação a investidores até o momento.',
  'project.publications.notifiedBadge': 'Notificado',
  'project.publications.quietTitle': 'Alcançados sem notificação',
  'project.publications.quietDescription':
    'Marcos que moveram a obra e foram registrados — e não foram comunicados a ninguém.',
  'project.publications.quietEmpty':
    'Todo marco alcançado neste empreendimento até o momento foi notificado. Nada passou em silêncio.',
  'project.publications.quietBadge': 'Sem notificação',
  'project.publications.countMilestones.one': '{count} marco',
  'project.publications.countMilestones.other': '{count} marcos',
  'project.publications.countUpdates.one': '{count} atualização publicada',
  'project.publications.countUpdates.other': '{count} atualizações publicadas',
  'project.publications.silenceNote':
    'O §13 torna a notificação seletiva de propósito: {milestones} e {updates} neste empreendimento saíram sem interromper ninguém, e todos continuam visíveis no portal. Silêncio não é retenção.',
  'project.publications.ahead.one': '{count} marco ainda por vir, com {next} em {date}.',
  'project.publications.ahead.other': '{count} marcos ainda por vir, com {next} em {date}.',
  'project.publications.nextInspection': 'a próxima vistoria',
  'project.publications.nextOne': 'o próximo',
  'project.publications.otherSideTitle': 'Abrir o outro lado',
  'project.publications.otherSideDescription':
    'O portal exibe o subconjunto liberado exatamente como o investidor o recebe.',
  'project.publications.viewAsInvestor': 'Ver {project} como um investidor vê',
  'project.publications.holderNote.one':
    '— conectado como cotista, com apenas a {count} atualização liberada e seus documentos publicados.',
  'project.publications.holderNote.other':
    '— conectado como cotista, com apenas as {count} atualizações liberadas e seus documentos publicados.',
  'reports.title': 'Relatórios',
  'reports.intro':
    'Todo número de um relatório vem dos mesmos registros que esta plataforma mostra, então um relatório não pode divergir da tela de onde foi tirado.',
  'reports.generateTitle': 'Gerar um relatório',
  'reports.generateDescription':
    'Escolha o que o relatório cobre. Ele é montado a partir da base no momento em que é solicitado.',
  'reports.development': 'Empreendimento',
  'reports.searchDevelopments': 'Buscar empreendimentos…',
  'reports.scope': 'Abrangência',
  'reports.scopeLabel': 'Abrangência do relatório',
  'reports.period': 'Período',
  'reports.searchPeriods': 'Buscar períodos…',
  'reports.generate': 'Gerar relatório',
  'reports.generateHint':
    'Selecione um empreendimento, uma abrangência e um período para montar a solicitação.',
  'reports.requestedTitle': 'Solicitados nesta sessão',
  'reports.requestedEmpty': 'Nenhum relatório solicitado ainda.',
  'reports.requestedAt': 'Solicitado em {date}',
  'reports.registerTitle': 'Registro de relatórios gerados',
  'reports.registerDescription': 'O que a plataforma já produziu para cada empreendimento.',
  'reports.kind': 'Tipo',
  'reports.kindLabel': 'Filtrar por tipo',
  'reports.kind.period': 'Período',
  'reports.kind.phase': 'Etapa',
  'reports.kind.final': 'Final',
  'reports.showing': 'Mostrando {shown} de {total} relatórios.',
  'reports.emptyTitle': 'Nenhum relatório corresponde a estes filtros',
  'reports.emptyBody':
    'Nada no registro corresponde ao tipo, ao empreendimento e ao período selecionados em conjunto.',
  'reports.heldInTotal.one': 'Há {count} relatório no registro.',
  'reports.heldInTotal.other': 'Há {count} relatórios no registro.',
  'reports.caption': 'Relatórios gerados, filtrados por tipo, empreendimento e período',
  'reports.column.report': 'Relatório',
  'reports.column.generated': 'Gerado em',
  'reports.column.by': 'Por',
  'reports.column.pages': 'Páginas',
  'reports.column.state': 'Situação',
  'reports.notGenerated': 'Não gerado',
  'reports.footnote':
    'O relatório final é gerado automaticamente quando o empreendimento é concluído e vendido, por isso ele aparece aqui antes de ter sido produzido.',
  'reports.toast.requestedTitle': 'Relatório solicitado',
  'reports.allDevelopments': 'Todos os empreendimentos',
  'reports.wholeProject': 'Empreendimento inteiro',
  'reports.allPeriods': 'Todos os períodos',
  'report.kind.period': 'Relatório de período',
  'report.kind.phase': 'Relatório de etapa',
  'report.kind.final': 'Relatório final',
  'report.notFoundTitle': 'Relatório não encontrado',
  'report.notFoundHeading': 'Relatório inexistente',
  'report.notFoundMessage':
    'Nada corresponde a "{id}". Ele pode ter sido retirado, ou o link pode estar desatualizado.',
  'report.backToRegister': 'Voltar para o registro de relatórios',
  'report.unknownDevelopment': 'Empreendimento desconhecido',
  'report.pages.one': '{pages} página',
  'report.pages.other': '{pages} páginas',
  'report.notGeneratedTitle': 'Este relatório ainda não foi gerado',
  'report.notGeneratedBody':
    'O relatório final é produzido a partir dos registros do próprio empreendimento quando a obra é concluída e a última unidade é vendida. Até lá não há nada para visualizar — não existem números, texto nem documento para mostrar.',
  'report.financialTitle': 'Resumo financeiro',
  'report.financialDescription':
    'Todos os números abaixo vêm do mesmo cronograma de etapas do painel do empreendimento.',
  'report.budgeted': 'Orçado',
  'report.budgetedHint': 'O orçamento por etapa, em todos os serviços.',
  'report.spentHint': 'Custo aprovado lançado contra o cronograma.',
  'report.varianceHint': 'Custo incorrido menos os {amount} orçados para as etapas em andamento.',
  'report.unspent': 'Saldo não gasto',
  'report.unspentHint': 'Aportes realizados menos o que foi efetivamente gasto.',
  'report.spendAgainstBudget': '{title} — gasto sobre o orçamento',
  'report.updatesHeading': 'Atualizações publicadas',
  'report.updatesDescription':
    'Só as atualizações revisadas e aprovadas chegam ao portal do investidor. O que ainda está em rascunho ou em revisão fica interno, por isso esta seção mostra menos atualizações do que a fila de publicação.',
  'report.updatesEmptyTitle': 'Nenhuma atualização publicada ainda',
  'report.updatesEmptyBody':
    'Nada foi aprovado e publicado neste empreendimento, então o portal do investidor não mostrou nada a este investidor.',
  'report.publishedOn': 'Publicado em {date}',
  'report.preparedBy': 'Preparado por',
  'report.approvedBy': 'Aprovado por',
  'report.photos': 'Fotos',
  'report.documents': 'Documentos',
  'report.evidenceTitle': 'Evidências publicadas',
  'report.evidenceDescription.one':
    '{count} foto publicada, agrupada na ordem das etapas. Este é o subconjunto liberado para o portal do investidor, não o acervo interno completo — os dados de exemplo não trazem arquivo de imagem, então cada registro aparece como uma legenda.',
  'report.evidenceDescription.other':
    '{count} fotos publicadas, agrupadas na ordem das etapas. Este é o subconjunto liberado para o portal do investidor, não o acervo interno completo — os dados de exemplo não trazem arquivo de imagem, então cada registro aparece como uma legenda.',
  'report.phasesTitle': 'Custos por etapa',
  'report.phasesDescription':
    'Custo orçado e incorrido por etapa. A linha de totais fecha com o resumo financeiro acima.',
  'report.phasesCaption': 'Custo orçado e incorrido de cada etapa',
  'report.column.phase': 'Etapa',
  'report.column.budgeted': 'Orçado',
  'report.column.actual': 'Incorrido',
  'report.column.variance': 'Variação',
  'report.column.used': 'Utilizado',
  'report.phaseCount.one': '{count} etapa',
  'report.phaseCount.other': '{count} etapas',
  'notifications.title': 'Notificações',
  'notifications.intro':
    'O que o escritório foi informado e — separadamente — o que o investidor foi informado. Uma notificação nasce de uma decisão, então um registro pode mudar de situação sem interromper ninguém.',
  'notifications.logMetric': 'No histórico',
  'notifications.unreadMetric': 'Não lidas',
  'notifications.unreadNone': 'Tudo já foi lido.',
  'notifications.unreadSome': 'Aguardando leitura.',
  'notifications.milestonesMetric': 'Marcos notificados',
  'notifications.milestonesHint': 'De {total} marcos alcançados até o momento.',
  'notifications.quietMetric': 'Liberados em silêncio',
  'notifications.quietHint':
    'Publicado para os investidores sem notificação — o §13 torna o anúncio opcional.',
  'notifications.history': 'Histórico de notificações',
  'notifications.historyHint':
    'A fila do próprio escritório, do evento mais recente para o mais antigo.',
  'notifications.markedTitle': 'Todas marcadas como lidas',
  'notifications.markedBody.one': '{count} notificação resolvida.',
  'notifications.markedBody.other': '{count} notificações resolvidas.',
  'notifications.category': 'Categoria',
  'notifications.filterLabel': 'Filtrar notificações por categoria',
  'notifications.filterAll': 'Todas as {count}',
  'notifications.filterCategory': '{category} {count}',
  'notifications.emptyTitle': 'Nada nesta categoria',
  'notifications.emptyBody': 'Nenhuma notificação do histórico tem a categoria que você filtrou.',
  'notifications.unreadSuffix': '(não lida)',
  'notifications.category.info': 'Informação',
  'notifications.category.success': 'Sucesso',
  'notifications.category.warning': 'Alerta',
  'notifications.category.error': 'Erro',
  'notifications.read': 'Lida',
  'notifications.markAsRead': 'Marcar como lida',
  'notifications.reachedTitle': 'O que chegou ao investidor',
  'notifications.reachedDescription':
    'Todos os marcos da plataforma, com a decisão de notificação ao lado de cada um.',
  'notifications.ahead': 'Por vir',
  'notifications.notified': 'Notificado',
  'notifications.noNotification': 'Sem notificação',
  'notifications.summary.one':
    '{count} marco gerou uma notificação a investidores e {quiet} foram alcançados sem nenhuma. Um marco é um fato da obra; se ele será anunciado é uma decisão à parte, tomada aqui.',
  'notifications.summary.other':
    '{count} marcos geraram uma notificação a investidores e {quiet} foram alcançados sem nenhuma. Um marco é um fato da obra; se ele será anunciado é uma decisão à parte, tomada aqui.',
  'notifications.preferencesTitle': 'Preferências de envio',
  'notifications.preferencesDescription':
    'As configurações do gestor para o que é anunciado, e como.',
  'notifications.selectiveTitle': 'Seletiva por princípio',
  'notifications.selectiveBody':
    'Suprimir uma notificação nunca suprime o registro. Uma atualização publicada sem notificação já está no portal do investidor no momento em que é liberada — a configuração só decide se alguém será interrompido para saber disso.',
  'notifications.channelLabel': 'Canal de envio',
  'notifications.channelSavedTitle': 'Canal de envio salvo',
  'notifications.channelSavedBody': 'As notificações aos investidores agora saem por {channel}.',
  'notifications.channel.email': 'E-mail',
  'notifications.channel.emailHint': 'Cada notificação é enviada no momento em que acontece.',
  'notifications.channel.emailPhrase': 'e-mail',
  'notifications.channel.inApp': 'Somente no aplicativo',
  'notifications.channel.inAppHint': 'Nada sai da plataforma; os cotistas veem quando entram.',
  'notifications.channel.inAppPhrase': 'somente no aplicativo',
  'notifications.channel.digest': 'Resumo semanal',
  'notifications.channel.digestHint': 'Uma mensagem por semana, reunindo tudo o que aconteceu.',
  'notifications.channel.digestPhrase': 'resumo semanal',
  'notifications.eventsLabel': 'Eventos que geram notificação',
  'notifications.event.milestone': 'Marco alcançado',
  'notifications.event.milestoneHint':
    'Um marco de etapa ou de vistoria é registrado em um empreendimento.',
  'notifications.event.variance': 'Variação de orçamento acima de {threshold}',
  'notifications.event.varianceHint':
    'O incorrido se afasta do orçamento por etapa além do limite.',
  'notifications.event.release': 'Atualização publicada liberada',
  'notifications.event.releaseHint':
    'Uma atualização passou pela barreira e ficou visível no portal do investidor.',
  'notifications.event.call': 'Chamada de capital emitida',
  'notifications.event.callHint':
    'Uma chamada de capital vai para os investidores com participação.',
  'notifications.eventEnabled': 'Notificação ativada',
  'notifications.eventSilenced': 'Notificação silenciada',
  'notifications.eventOnBody': '{event} vai gerar uma notificação.',
  'notifications.eventOffBody':
    '{event} não vai mais interromper ninguém. O registro continua aparecendo no portal.',
  'notifications.eventsSummary':
    '{active} de {total} tipos de evento geram notificação ativa, entregue por {channel}.',

  /* ---- Investor portal (GF) ---- */
  'portal.home.title': 'Sua carteira',
  'portal.home.summary.one': 'Um empreendimento em que você tem participação',
  'portal.home.summary.other': '{count} empreendimentos em que você tem participação',
  'portal.home.lastUpdated': ', com a atualização mais recente em {date}',
  'portal.home.totalsHeading': 'Seus totais',
  'portal.home.committed': 'Capital comprometido',
  'portal.home.committedHint': 'O total que você comprometeu em suas participações.',
  'portal.home.contributed': 'Aportes realizados',
  'portal.home.contributedHint': 'Capital chamado e liquidado até o momento.',
  'portal.home.shareValue': 'Valor da sua participação',
  'portal.home.shareValueHint':
    'Seu percentual de participação em cada empreendimento, aplicado ao capital comprometido nele.',
  'portal.home.unspent': 'Não gasto nos seus empreendimentos',
  'portal.home.unspentHint':
    'Capital comprometido nos seus empreendimentos e ainda não gasto, na sua proporção.',
  'portal.home.developmentsHeading': 'Seus empreendimentos',
  'portal.home.share': '{share} de participação',
  'portal.home.progress': 'Progresso',
  'portal.home.currentPhase': 'Etapa atual',
  'portal.home.latestUpdate': 'Última atualização',
  'portal.home.nonePublished': 'Nenhuma publicada',
  'portal.home.viewDevelopment': 'Ver empreendimento',
  'portal.home.latestPanelTitle': 'Atualização liberada mais recente',
  'portal.home.released': 'Liberada em {date}',
  'portal.home.photos': 'Fotos',
  'portal.home.documents': 'Documentos',
  'portal.home.approvedBy': 'Aprovado por',
  'portal.home.releasedLabel': 'Liberada em',
  'portal.home.readUpdate': 'Ler a atualização completa',
  'portal.home.developmentFallback': 'Empreendimento',
  'portal.project.share': '{share} de participação',
  'portal.project.progress': 'Progresso',
  'portal.project.notInPortfolio': 'Fora da sua carteira',
  'portal.project.refusedTitle': 'Este empreendimento não faz parte da sua carteira',
  'portal.project.refusedBody':
    'O acesso do investidor é limitado aos empreendimentos em que você tem participação. Se você esperava ver este empreendimento, o seu gerente de relacionamento pode confirmar as suas posições.',
  'portal.project.backToPortfolio': 'Voltar para a sua carteira',
  'portal.project.backLink': 'Sua carteira',
  'portal.project.participationTitle': 'Sua participação',
  'portal.project.participatingSince': 'Participando desde {date}.',
  'portal.project.yourShare': 'Sua participação',
  'portal.project.yourShareHint': 'Seu percentual de participação neste empreendimento.',
  'portal.project.committed': 'Capital comprometido',
  'portal.project.contributed': 'Aportes realizados',
  'portal.project.contributedHint': 'Capital chamado e liquidado até o momento.',
  'portal.project.capitalCalls': 'Chamadas de capital',
  'portal.project.callsSettledHint': 'liquidadas das emitidas',
  'portal.project.ownershipTitle': 'Composição societária',
  'portal.project.retainedByBuilder': 'Retido pela incorporadora',
  'portal.project.heldByInvestors': 'Detido por investidores em contratos de participação',
  'portal.project.yourParticipation': 'Sua participação',
  'portal.project.ownershipNote':
    'As posições individuais de cada investidor são divulgadas apenas ao próprio investidor, por isso o quadro é apresentado aqui sem nomes e sem valores.',
  'portal.project.scheduleTitle': 'Cronograma',
  'portal.project.scheduleDescription':
    'Previsto contra realizado, por etapa. As etapas não são sequenciais — cada uma é posicionada na janela do cronograma, então uma sobreposição indica duas etapas que realmente ocorrem ao mesmo tempo.',
  'portal.project.legendActualInProgress': 'Realizado, em andamento',
  'portal.project.legendActualCompleted': 'Realizado, concluído',
  'portal.project.budgeted': '{amount} orçados',
  'portal.project.actual': '{amount} realizados',
  'portal.project.noSpend': 'sem custo incorrido',
  'portal.project.phaseFallback': 'Geral',
  'portal.project.milestonesTitle': 'Marcos',
  'portal.project.milestonesNext': 'Próximos',
  'portal.project.noUpcomingMilestones': 'Não há outros marcos previstos para este empreendimento.',
  'portal.project.milestonesReached': 'Alcançados e comunicados',
  'portal.project.noReachedMilestones':
    'Nenhum marco foi comunicado aos investidores até o momento.',
  'portal.project.approvedTitle': 'Resumo financeiro aprovado',
  'portal.project.approvedDescription':
    'Os números conforme foram divulgados a você, e não como estão na plataforma hoje.',
  'portal.project.spendToDate': 'Custo incorrido até o momento',
  'portal.project.spendToDateHint':
    'Custo total do empreendimento divulgado na atualização publicada abaixo.',
  'portal.project.againstForecast': 'Contra o previsto',
  'portal.project.againstForecastHint':
    'Negativo significa que o período fechou abaixo do previsto publicado para ele.',
  'portal.project.yourShareUnspent': 'Sua participação, não gasta',
  'portal.project.yourShareUnspentHint':
    'Capital comprometido neste empreendimento e ainda não gasto, na sua proporção.',
  'portal.project.disclosedIn': 'Divulgado em',
  'portal.project.disclosedRest':
    ', período {period}, aprovado por {approvedBy} em {approvedAt} e liberado a você em {publishedAt}.',
  'portal.project.noApprovedYet':
    'Nenhum número financeiro foi aprovado para liberação neste empreendimento até o momento. Nada aparece aqui enquanto uma atualização que os contenha não for aprovada e publicada.',
  'portal.project.evidenceTitle': 'Evidências publicadas',
  'portal.project.evidenceCount.one':
    '{count} item liberado para você, do registro fotográfico do empreendimento.',
  'portal.project.evidenceCount.other':
    '{count} itens liberados para você, do registro fotográfico do empreendimento.',
  'portal.project.noEvidence': 'Nenhuma evidência foi liberada neste empreendimento até o momento.',
  'portal.project.capturedBy': 'Registrado por {name}',
  'portal.project.documentsTitle': 'Documentos liberados',
  'portal.project.documentsDescription':
    'Documentos autorizados para a sua participação neste empreendimento.',
  'portal.project.openDocuments': 'Abrir documentos',
  'portal.project.noDocuments': 'Nenhum documento foi liberado neste empreendimento até o momento.',
  'portal.updates.title': 'Atualizações publicadas',
  'portal.updates.intro':
    'Cada atualização abaixo foi revisada e aprovada antes de chegar até você, e traz o nome de quem a aprovou. Nada da fila de publicação aparece aqui.',
  'portal.updates.filterLabel': 'Filtrar atualizações por empreendimento',
  'portal.updates.bothDevelopments': 'Ambos os empreendimentos',
  'portal.updates.count.one': '{count} atualização publicada',
  'portal.updates.count.other': '{count} atualizações publicadas',
  'portal.updates.countScoped.one': '{count} atualização publicada de {project}',
  'portal.updates.countScoped.other': '{count} atualizações publicadas de {project}',
  'portal.updates.emptyTitle': 'Nenhuma atualização publicada',
  'portal.updates.emptyBody': 'Nada foi aprovado para liberação nesta seleção até o momento.',
  'portal.updates.publishedBadge': 'Publicada em {date}',
  'portal.updates.photos': 'Fotos',
  'portal.updates.documents': 'Documentos',
  'portal.updates.approvedBy': 'Aprovado por',
  'portal.updates.period': 'Período',
  'portal.updates.readUpdate': 'Ler a atualização completa',
  'portal.updates.developmentFallback': 'Empreendimento',
  'portal.update.notAvailableHeading': 'Atualização não disponível',
  'portal.update.refusedTitle': 'Esta atualização não está disponível para você',
  'portal.update.refusedBody':
    'Ou ela não foi publicada, ou pertence a um empreendimento fora da sua carteira. Aqui são mostradas apenas as atualizações aprovadas das suas próprias participações.',
  'portal.update.backToUpdates': 'Voltar para as atualizações publicadas',
  'portal.update.developmentFallback': 'Empreendimento',
  'portal.update.backLink': 'Atualizações publicadas',
  'portal.update.publishedBadge': 'Publicada em {date}',
  'portal.update.periodBadge': 'Período coberto: {period}',
  'portal.update.approvalTitle': 'Aprovado para liberação a você',
  'portal.update.preparedBy': 'Elaborado por',
  'portal.update.reviewedBy': 'Revisado por',
  'portal.update.approvedBy': 'Aprovado por',
  'portal.update.approvedOn': 'Aprovado em',
  'portal.update.recordNote':
    'Este é o registro aprovado do período. Ele foi liberado a você em {date}, após a revisão acima, e é a versão com a qual você pode contar.',
  'portal.update.periodTitle': 'O período',
  'portal.update.figuresTitle': 'Números divulgados nesta atualização',
  'portal.update.figuresDescription': 'A posição de custos conforme aprovada para este período.',
  'portal.update.spendToDate': 'Custo incorrido até o momento',
  'portal.update.spendToDateHint': 'Custo total do empreendimento no fechamento deste período.',
  'portal.update.againstForecast': 'Contra o previsto',
  'portal.update.againstForecastHint':
    'Negativo significa que o período fechou abaixo do previsto publicado para ele.',
  'portal.update.evidenceTitle': 'Evidências liberadas com esta atualização',
  'portal.update.evidencePeriod':
    'Fotos publicadas de {period}. Nem toda foto tirada no canteiro é liberada, e só as liberadas aparecem aqui.',
  'portal.update.evidenceRecord': 'Fotos publicadas do registro liberado deste empreendimento.',
  'portal.update.noEvidence': 'Nenhuma foto foi liberada neste empreendimento até o momento.',
  'portal.update.phaseFallback': 'Geral',
  'portal.update.capturedBy': 'Registrado por {name}',
  'portal.update.documentsTitle': 'Documentos liberados com esta atualização',
  'portal.update.documentsDescription':
    'Arquivos autorizados deste empreendimento. Os arquivos são solicitados na tela de documentos.',
  'portal.update.openDocuments': 'Abrir documentos',
  'portal.update.noDocuments': 'Nenhum documento foi liberado até o momento.',
  'portal.documents.title': 'Documentos e relatórios',
  'portal.documents.intro':
    'Os documentos autorizados para as suas participações e os relatórios gerados a partir deles. Um documento aparece aqui depois de verificado e liberado, e não quando foi recebido.',
  'portal.documents.filterLabel': 'Filtrar documentos por empreendimento',
  'portal.documents.bothDevelopments': 'Ambos os empreendimentos',
  'portal.documents.count.one': '{count} documento autorizado',
  'portal.documents.count.other': '{count} documentos autorizados',
  'portal.documents.reportCount.one': '{count} relatório publicado',
  'portal.documents.reportCount.other': '{count} relatórios publicados',
  'portal.documents.downloadTitle': 'Download preparado',
  'portal.documents.downloadBody':
    '{name} — o envio de arquivos não está habilitado neste ambiente.',
  'portal.documents.downloadLabel': 'Baixar {name}',
  'portal.documents.perProject.one': '{count} documento autorizado para a sua participação.',
  'portal.documents.perProject.other': '{count} documentos autorizados para a sua participação.',
  'portal.documents.listLabel': 'Documentos autorizados de {project}',
  'portal.documents.emptyProject':
    'Nenhum documento foi liberado neste empreendimento até o momento.',
  'portal.documents.reportsTitle': 'Relatórios publicados',
  'portal.documents.reportsDescription':
    'Gerados a partir do registro aprovado de cada período e etapa.',
  'portal.documents.noReports': 'Nenhum relatório foi publicado para esta seleção até o momento.',
  'portal.documents.reportScope.period': 'Relatório de período',
  'portal.documents.reportScope.phase': 'Relatório de etapa',
  'portal.documents.reportScope.final': 'Relatório final',
  'portal.documents.period': 'Período',
  'portal.documents.pages': 'Páginas',
  'portal.documents.generated': 'Gerado em',
  'portal.documents.preparedBy': 'Elaborado por {name}',
  'portal.documents.finalTitle': 'Relatório final',
  'portal.documents.finalDescription':
    'Emitido uma única vez, após a venda do empreendimento — a prestação de contas completa dele, e não de um período.',
  'portal.documents.notIssued': 'Ainda não emitido',
  'portal.documents.finalBody':
    'Quando o empreendimento é encerrado, o seu relatório final substitui os relatórios de período por um único documento: o resultado realizado contra o previsto, as contas de encerramento e o registro fotográfico completo, da aquisição à venda. Ele é gerado a partir do mesmo registro aprovado de tudo o que está acima, então nada nele pode contradizer o que foi informado a você ao longo do caminho.',
  'portal.documents.releasesAfterSale': 'Liberado após a venda, previsto para {date}',
  'portal.opportunities.title': 'Novas oportunidades',
  'portal.opportunities.intro':
    'Empreendimentos em estudo para o futuro. Esta área é discreta de propósito — é uma nota do que está por vir, mantida fora do caminho dos investimentos que você já tem.',
  'portal.opportunities.alertTitle': 'Nada aqui está aberto para participação',
  'portal.opportunities.alertBody':
    'Estes empreendimentos ainda estão em estudo, e as condições pelas quais um investidor poderia participar ainda não foram definidas. Não há captação em curso nem oferta sendo feita. O seu gerente de relacionamento entrará em contato diretamente caso um deles seja aberto, e você não precisará responder a nada nesta página.',
  'portal.opportunities.emptyTitle': 'Nenhuma oportunidade no momento',
  'portal.opportunities.emptyBody':
    'Não há empreendimentos em estudo para participação futura neste momento.',
  'portal.opportunities.projectedStart': 'Início previsto em {date}',
  'portal.opportunities.notOpen': 'Não está aberto para participação.',
  /* ==== END GROUPED CATALOGUE ENTRIES ==== */
};
